/**
 * useEmailDeliverabilityData
 *
 * DASH-EMAIL-007: real-data hook for the Email "Deliverability & Hygiene"
 * (Recipient Health) view -- replaces the prior mock-adapter shell phase
 * with the real, read-only RECIPIENT-grain Dashboard API
 * (emailDeliveryRecipientHealthApi.js -> GET /api/email-delivery/
 * dashboard/recipient-health).
 *
 * Mirrors useEmailDashboardData's shape (loading/error/data/refresh) so
 * this file's own consumer (EmailDeliverabilityView.jsx) required no
 * structural change beyond the addition of `providerSuppressed` to its
 * hygiene-state config (see that component's own comment) -- the hook's
 * return shape stays otherwise identical to the prior shell-phase
 * contract.
 *
 * Deliberately accepts only `environment` -- the one query concept this
 * page has ever supported (see EmailFilterBar.jsx's
 * EMAIL_FILTER_BAR_FIELDS.environmentOnly). No date-range/campaign/
 * audience/sender parameter is accepted or forwarded: this hook's
 * signature never had those params to begin with, and
 * sendgrid_hygiene_recipient_state is a true current-state projection
 * with no independent campaign/audience/sender concept at all -- there is
 * no "irrelevant filter" to narrow here, because none was ever accepted.
 *
 * Always fetches BOTH real SendGridEnvironment values (qualification and
 * production) in parallel, regardless of the requested `environment` --
 * this is what `environmentBreakdown` needs (it always shows both,
 * exactly like the prior mock's own documented contract), and it lets
 * "all" be computed here, client-side, as the additive union of the two
 * real per-environment counts -- the SAME arithmetic the prior mock
 * adapter used (never a scale/multiplier, never a third backend request
 * for "all"). Two real, cheap, indexed aggregations per page load, never
 * an event-log scan (see the backend service's own docstring).
 */

import { useState, useEffect, useCallback } from "react";
import { getEmailDeliveryRecipientHealth } from "../lib/emailDeliveryRecipientHealthApi";

const HYGIENE_KEYS = {
  untested: "untested",
  delivery_confirmed: "deliveryConfirmed",
  temporarily_undeliverable: "temporarilyUndeliverable",
  invalid: "invalid",
  suppressed: "suppressed",
  provider_suppressed: "providerSuppressed",
};

const ELIGIBILITY_KEYS = {
  eligible: "eligible",
  not_eligible: "notEligible",
  unknown: "unknown",
};

function emptyHygiene() {
  return {
    untested: 0,
    deliveryConfirmed: 0,
    temporarilyUndeliverable: 0,
    invalid: 0,
    suppressed: 0,
    providerSuppressed: 0,
  };
}

function emptyEligibility() {
  return { eligible: 0, notEligible: 0, unknown: 0 };
}

function mapMetrics(metrics) {
  const hygiene = emptyHygiene();
  const eligibility = emptyEligibility();
  for (const [backendKey, frontendKey] of Object.entries(HYGIENE_KEYS)) {
    hygiene[frontendKey] = metrics?.[backendKey] ?? 0;
  }
  for (const [backendKey, frontendKey] of Object.entries(ELIGIBILITY_KEYS)) {
    eligibility[frontendKey] = metrics?.[backendKey] ?? 0;
  }
  const total = metrics?.total_recipients ?? 0;
  return { total, hygiene, eligibility };
}

function addHygiene(a, b) {
  const sum = emptyHygiene();
  for (const key of Object.keys(sum)) sum[key] = (a[key] || 0) + (b[key] || 0);
  return sum;
}

function addEligibility(a, b) {
  const sum = emptyEligibility();
  for (const key of Object.keys(sum)) sum[key] = (a[key] || 0) + (b[key] || 0);
  return sum;
}

async function fetchSource({ environment }) {
  const [qualificationResponse, productionResponse] = await Promise.all([
    getEmailDeliveryRecipientHealth({ environment: "qualification" }),
    getEmailDeliveryRecipientHealth({ environment: "production" }),
  ]);

  const qualification = mapMetrics(qualificationResponse?.metrics);
  const production = mapMetrics(productionResponse?.metrics);

  const environmentBreakdown = [
    { environment: "qualification", total: qualification.total, hygiene: qualification.hygiene },
    { environment: "production", total: production.total, hygiene: production.hygiene },
  ];

  const selected =
    environment === "qualification"
      ? qualification
      : environment === "production"
        ? production
        : {
            total: qualification.total + production.total,
            hygiene: addHygiene(qualification.hygiene, production.hygiene),
            eligibility: addEligibility(qualification.eligibility, production.eligibility),
          };

  const eligibility = selected.eligibility || qualification.eligibility;
  const total = selected.total;
  // Eligible/total is a real, safe rate: both counts are two independent
  // partitions of the exact SAME recipient population from the exact SAME
  // aggregation -- never a fabricated cross-collection denominator (see
  // the mock adapter's own former invariant assertion for the same
  // reasoning, now enforced server-side by construction instead).
  const eligibilityRate = total ? eligibility.eligible / total : 0;

  return {
    total,
    hygiene: selected.hygiene,
    eligibility,
    eligibilityRate,
    environmentBreakdown,
  };
}

export function useEmailDeliverabilityData(options = {}) {
  const { environment = "all" } = options;

  // Initialize as loading so the view renders its loading state on first
  // paint instead of briefly flashing empty content before the fetch
  // effect below has a chance to run -- same rationale as
  // useEmailDashboardData.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Depends only on `environment` -- the sole query concept this page
  // supports -- so this effect fires exactly once per Apply (environment
  // is draft/applied via the shared useEmailFilterState instance) and
  // never on a draft edit.
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchSource({ environment });
      setData(result);
    } catch (err) {
      setError(err?.message || "Failed to load Deliverability data");
    } finally {
      setLoading(false);
    }
  }, [environment]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loading,
    error,
    data,
    total: data?.total ?? 0,
    hygiene: data?.hygiene || null,
    eligibility: data?.eligibility || null,
    eligibilityRate: data?.eligibilityRate ?? 0,
    environmentBreakdown: data?.environmentBreakdown || [],
    refresh: fetchData,
  };
}

export default useEmailDeliverabilityData;
